'use client';

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SignupShell } from '../../components/SignupShell';
import { useFinishSignupFlow } from '../../hooks/useFinishSignupFlow';
import { useSignupApi } from '../../hooks/useSignupApi';
import { readWithFallback } from '../../services/read-diagnostics';
import { SIGNUP_RESUME_WAIT_MS, within } from '../../services/signup-resume';
import { useSignupFlowStore } from '../../stores/signup-flow-store';
import { SignupErrorContent } from './SignupErrorContent';
import { WelcomeBackContent } from './WelcomeBackContent';

/** The error body, for a held member or one whose details could not be read. */
function ErrorBodyContent() {
  useFinishSignupFlow();
  return <SignupErrorContent />;
}

/**
 * `/login-no-progress/`: a signed-in returning member signup cannot send to
 * the download page. One with no saved details is welcomed back (v9 4.3).
 * One whose details are saved but who is held (a minor stop, consent
 * pending), or whose details cannot be read, keeps the error body: their
 * answers were not lost, or we cannot tell. The login code step passes what
 * it read; the page reads again only when that is unknown (a reload, or a
 * failed read at the code step), and that read is bounded. A visit with
 * no one signed in is welcomed back plainly. The page keeps its screen id.
 */
export function LoginNoProgressScreen() {
  const api = useSignupApi();
  const [opened] = useState(() => {
    const state = useSignupFlowStore.getState();
    return { signedIn: state.signInContact !== null, known: state.loginProfileState };
  });
  const needsRead = opened.signedIn && opened.known === null;
  const profile = useQuery({
    queryKey: ['signup', 'login-no-progress', 'profile'] as const,
    queryFn: () =>
      readWithFallback('registration_profile_on_welcome_back', 'show_error_body', () =>
        within(api.getRegistrationProfile(), SIGNUP_RESUME_WAIT_MS),
      ),
    enabled: needsRead,
    staleTime: Infinity,
    gcTime: 0,
  });
  if (needsRead && profile.isPending) return <SignupShell>{null}</SignupShell>;
  const unsaved = !opened.signedIn
    ? true
    : opened.known !== null
      ? opened.known === 'unsaved'
      : profile.data != null && profile.data.completedAt === null;
  return <SignupShell>{unsaved ? <WelcomeBackContent /> : <ErrorBodyContent />}</SignupShell>;
}
